"use client";

import { useMemo } from "react";

import { BigChordNotes, type BigChordNote } from "@/components/ui/molecules/big-chord-notes";
import type { ChordProse } from "@/components/learn-piece/s0-copy";
import { FONT_MONO, GOLD, GREEN, RED, VIOLET_BRIGHT } from "@/components/ui/organisms/chord-practice/palette";
import { useActivePianoNotes } from "@/hooks/use-active-piano-notes";
import { useNoteLabel } from "@/hooks/use-notation";
import type { ChordNoteColumn } from "@/lib/harmony/chord-notes";
import { positionLabel, positionShort } from "@/lib/harmony/position";
import { takeGlow } from "@/lib/harmony/take";
import type { Frieze } from "@/lib/harmony/frieze";
import { ScoreRail } from "./score-rail";

/**
 * L'accord du niveau, posé au milieu de la scène.
 *
 * De haut en bas : le nom de l'accord et celui qui vient, les notes en grand
 * avec leurs doigtés, et le temps qu'il reste sur cette fenêtre du morceau.
 *
 * Le dessin des notes est celui de l'écran d'exercice d'accords et il est **le
 * même composant**, `BigChordNotes`. Ce qui reste ici est ce que ce dessin ne
 * doit pas connaître : les touches du piano, les deux mains, la fenêtre du
 * morceau qui s'écoule, la lueur d'ambiance et la boîte qui donne leur unité
 * aux `cq`.
 *
 * ## Le nom en toutes lettres, au-dessus des notes qu'il désigne
 *
 * `Ré mineur` et non `Dm` : le nom et ses notes sont à dix pixels l'un de
 * l'autre, et les voir se contredire fait douter de la seule chose que cet
 * écran affirme. C'est la notation des **notes** qui décide, pas celle des
 * symboles d'accord ; le symbole garde le rail, qui est un chiffrage.
 *
 * Le suivant est à droite, plus petit et plus discret : on doit comprendre que
 * c'est ce qui vient sans jamais le confondre avec ce qu'on prend. C'est le
 * seul endroit où l'écran nomme l'avenir en permanence, et c'est pour ça que le
 * bandeau ne l'annonce plus.
 *
 * ## Le nom écrase les lettres, et c'est le nom qu'on apprend
 *
 * Ce qu'on emporte de ce niveau est « Ré mineur », pas « ré, fa, la » : la
 * liste épelle, elle ne se retient pas. Le nom prend donc le double de la
 * hauteur des lettres, alors qu'il en faisait le tiers.
 *
 * ## La fondamentale est teintée, la qualité reste blanche
 *
 * Un nom d'accord dit deux choses de nature différente : **sur quelle note**
 * il se construit, et **ce qu'on empile dessus**. La note est celle qu'on
 * retrouve sous les doigts, à gauche de la liste juste en dessous ; la qualité
 * est un mot de théorie. Les peindre pareil laisse lire une seule masse.
 *
 * La coupe vient de `chordProseParts` et pas d'un découpage du texte fini :
 * « Si bémol majeur » fait trois mots dont les deux premiers sont la note, et
 * couper au premier espace teindrait « Si » en laissant « bémol » blanc.
 *
 * La liste rétrécit par sa **mesure** et non par un réglage de son dessin :
 * `BigChordNotes` est le dessin d'accord du produit et se règle sur la mesure
 * qu'on lui donne, jamais sur le goût de l'appelant. Il en reçoit donc une plus
 * étroite que la scène, et il en tire de lui-même des lettres plus petites. Le
 * plafond en hauteur, lui, continue de se lire sur la scène entière : les deux
 * axes ne cherchent pas le même repère.
 *
 * ## Le degré est posé à gauche du nom, pas devant lui
 *
 * Le nom reste sur l'axe des notes qu'il désigne : c'est la seule chose que
 * cet écran affirme, et elle ne se décale pas parce qu'un chiffre romain
 * l'accompagne. Le degré tient donc la piste de gauche de la grille, collé au
 * nom par la gouttière, et le centrage ne bouge pas d'un accord à l'autre.
 *
 * La lettre de fonction sous le chiffre porte la couleur de l'atlas — violet la
 * tonique, ambre la sous-dominante, rouge la dominante — et par `functionTagOf`,
 * la même fonction que la pastille du bandeau. Deux marques d'une seule
 * fonction, jamais deux réponses.
 *
 * ## Un seul rectangle pour le temps, des deux côtés du premier temps
 *
 * Avant le premier temps, il porte le décompte d'ouverture ; après, la barre
 * qui s'écoule. Les deux répondent à la même question, combien de temps
 * encore, et deux emplacements en feraient deux objets là où il n'y en a
 * qu'un.
 *
 * ## La lueur monte pendant tout le passage
 *
 * Sombre à l'arrivée sur l'accord, elle se remplit de la frappe jusqu'à la
 * dernière note, puis retombe d'un coup sur l'accord suivant, qui recommence à
 * attendre. Elle mesure donc le temps qu'on passe sur cet accord-ci.
 *
 * Elle ne peut pas se confondre avec la barre du temps juste en dessous, qui
 * parcourt la même durée dans l'autre sens : la barre est un objet net, petit,
 * gradué, posé en bas, et elle se **vide** ; la lueur est un fond diffus, sans
 * bord, et elle se **remplit**. Sens opposés, formes opposées, échelles
 * opposées, et aucune des deux ne répète l'autre.
 *
 * Deux couches d'une seule lumière, le repos et le passage. Même géométrie pour
 * les deux, par construction — d'où `sceneGlow` — et seule l'opacité les anime,
 * donc le compositeur fait tout le travail. Aucune boucle ne tourne : le studio
 * a retiré ses `requestAnimationFrame` autonomes pour cesser de chauffer au
 * repos, et la durée du passage étant connue d'avance, une transition CSS
 * suffit à la parcourir sans une image de JavaScript.
 *
 * ## Le compte à rebours est ce qui met en rythme
 *
 * La barre s'écoule sur la durée exacte du passage, celle qu'on entend, et elle
 * part avec lui : avant la frappe il n'y a rien à mesurer, donc le rail reste
 * vide. En CSS et non en JavaScript : une animation qui part au montage ne
 * coûte pas un rendu, là où une barre pilotée par une boucle en coûterait
 * soixante par seconde pour une largeur.
 *
 * ## Ce qui se joue est nommé sur le rail, pas ici
 *
 * La ligne d'accord ne nomme que l'accord : depuis la basse publiée, la main
 * gauche traverse des notes de passage, et les faire entrer dans la ligne
 * écrivait quatre noms pour une triade. Voir `chord-notes.ts`.
 *
 * Ce que la gauche joue a d'abord été posé à côté des lettres, derrière un
 * séparateur, avec la file de ce qui venait. Trop loin du rail : un nom de
 * touche répond à « quoi jouer », question qui se pose à l'instant de la
 * frappe, et le rail est seul à situer cet instant. Les deux voies y portent
 * donc leurs noms, la gauche ses touches et la droite son accord, et le rail
 * écrit aussi ce que chaque main jouera après la fenêtre. Voir
 * `score-rail.tsx`.
 *
 * Ce qui reste ici est l'**annonce** de l'accord suivant, sous les lettres,
 * avec sa première basse, la flèche qui y mène et sa pastille de renversement.
 * Elle dit désormais deux fois ce que la marge droite du rail dit une : le nom
 * de l'accord suivant et sa basse. Son retrait est une décision de produit et
 * pas une conséquence technique, donc elle reste, et la position de l'accord
 * suivant n'est écrite qu'ici.
 *
 * ## Ce que « prise » veut dire ici, et pourquoi ce n'est pas ailleurs
 *
 * Une note est prise quand **toutes** ses touches sont enfoncées, et pas quand
 * l'une d'elles l'est. `Ré · Fa · La` a trois colonnes pour quatre touches au
 * niveau 1, puisque la fondamentale est sous les deux mains. Exiger les deux
 * rend le dessin fidèle à la règle qui fait avancer : « les trois noms sont
 * allumés » et « l'accord est pris » deviennent la même phrase, et le joueur
 * n'a jamais à deviner pourquoi l'écran n'avance pas alors que tout a l'air
 * allumé.
 *
 * L'exercice d'accords, lui, valide à l'octave près et d'une seule main. Deux
 * définitions de la même chose, ce qui est exactement la raison pour laquelle
 * `BigChordNotes` reçoit `held` au lieu de le calculer.
 *
 * Les chiffres s'allument main par main, sur leur propre touche : ils disent
 * laquelle des deux manque. Aucune couleur d'erreur, aucun compte.
 */

/**
 * Le corps du nom d'accord, dont dépendent deux choses et pas une.
 *
 * Sa taille, bien sûr, et la remontée du bloc « ce qui vient », qui se cale sur
 * sa **ligne de base** et non sur le bas de sa ligne. Les deux doivent donc
 * suivre la même valeur fluide, sans quoi le bloc dériverait à chaque largeur
 * d'écran. Une constante, et le lien devient impossible à rompre.
 *
 * ## Il a reculé d'un dixième pour que le rail porte deux lignes de noms
 *
 * Le rail écrit désormais ce que **les deux** mains jouent, une ligne de noms
 * par voie, et cette seconde ligne se paie : la colonne n'a plus de marge sous
 * sept cents pixels de haut, et ses `clamp()` sont à leur plancher dès la
 * posture de référence. Le nom et les lettres reculent donc ensemble, du même
 * dixième — `NOTES_MEASURE` porte l'autre moitié — pour que la composition
 * garde son rapport au lieu de se tasser d'un côté.
 *
 * Les trois bornes suivent le même facteur, plancher et plafond compris : le
 * plafond seul décide sur un grand écran, et l'y laisser aurait fait grossir le
 * nom par rapport aux lettres là où justement il y a de la place.
 */
const NAME_SIZE = "min(clamp(25px, 9.4cqw, 115px), 26cqh)";

/**
 * La marge latérale du contenu de la scène, et la largeur qu'elle lui laisse.
 *
 * Deux choses en dépendent, la boîte du contenu et la mesure des lettres, et
 * elles doivent suivre la même valeur : une constante, et le lien ne peut plus
 * se défaire.
 *
 * En `cqw` de la scène et non en pourcentage du parent : c'est ce qui permet à
 * la mesure d'être une longueur qu'on nomme au lieu d'une boîte qu'on pose.
 */
const CONTENT_PAD = 18;
const ROW_WIDTH = `calc(100cqw - ${CONTENT_PAD * 2}px)`;

/**
 * La mesure des lettres, en part de la scène.
 *
 * `BigChordNotes` règle sa largeur sur cette longueur ; lui en donner moins de
 * la moitié est ce qui met le nom au-dessus de la liste. Le plancher en pixels
 * garde les trois noms dans leur boîte sur un écran étroit, où le dessin
 * s'arrête de rétrécir avant elle.
 *
 * ## Une longueur nommée, et non plus une boîte
 *
 * Le dessin a longtemps reçu cette mesure sous la forme d'une boîte de cette
 * largeur, en `container-type: inline-size`, dont il tirait ses `cqw`. Mais une
 * boîte qui porte la mesure est plus large que le dessin par construction — le
 * dessin des trois lettres fait deux cent trente pixels dans une mesure de trois
 * cent soixante-onze — et le dessin s'y centre. La basse, posée dans la piste
 * d'à côté, se trouvait donc à soixante-dix pixels de la première lettre sans
 * que rien ne le dise.
 *
 * La même valeur passe désormais par `measure`. La piste du milieu redevient
 * `auto`, elle épouse le dessin, la basse se pose contre lui, et les lettres
 * gardent exactement le corps qu'elles avaient. Voir `big-chord-notes.tsx` pour
 * pourquoi une boîte de mesure ne peut pas épouser son contenu.
 *
 * ## Elle a reculé d'un dixième, et le plancher du dessin absorbe le recul
 *
 * Même dixième que `NAME_SIZE`, pour la même raison : le rail porte une seconde
 * ligne de noms. Attention à ce que cette valeur peut et ne peut pas faire —
 * `BigChordNotes` lit une part de la mesure puis la borne à quarante-huit
 * pixels, et sur cet écran la part vaut trente-huit : c'est le plancher qui
 * décide, et le recul n'a donc **aucun effet** sous mille quatre cents pixels de
 * large. Il en a un au-delà, où la part reprend la main. Ce n'est pas une valeur
 * morte, c'est une valeur que le dessin n'écoute que là où il a le choix.
 */
const NOTES_MEASURE = `min(${ROW_WIDTH}, max(calc(${ROW_WIDTH} * 0.38), 290px))`;

/**
 * La phrase du niveau, sous son surtitre.
 *
 * Les mesures de la maquette « Accord v12 », dans la police du produit : fine,
 * assez grande pour se lire à un mètre du pupitre, et une mesure étroite pour
 * qu'elle se coupe en deux lignes équilibrées plutôt qu'en une seule très
 * longue. Deux lignes réservées, qu'elle soit là ou non.
 */
const EXPLANATION_SIZE = "clamp(18px, 2.6vh, 25px)";
const EXPLANATION_LINE_HEIGHT = 1.34;
const EXPLANATION_HEIGHT = `${2 * EXPLANATION_LINE_HEIGHT}em`;
const EXPLANATION_MEASURE = "min(46ch, 92%)";
const EXPLANATION_INK = "#b6b4c0";
const EXPLANATION_LABEL_INK = "color-mix(in srgb, #b9a7f7 62%, #56545f)";

/**
 * Les deux violets de la lueur : son cœur, et le palier d'où elle s'éteint.
 *
 * Le même violet que partout, à deux opacités relevées : `VIOLET_SOFT` était
 * réglé pour un panneau, et étalé sur toute une scène il ne se voyait plus.
 */
const SCENE_GLOW_CORE = "rgba(139, 92, 246, 0.16)";
const SCENE_GLOW_EDGE = "rgba(139, 92, 246, 0.09)";

/**
 * La lueur du passage, plus présente que celle du repos.
 *
 * Le même violet, plus dense : c'est une montée en présence et non un
 * changement de couleur, donc la lumière reste la même et seule sa quantité
 * parle. Elle ne dit aucune note, contrairement à l'ambre de la sous-dominante
 * qui est une lettre dans le cadre du degré, et rien d'autre sur cet écran
 * n'est violet plein.
 */
const TAKE_GLOW_CORE = "rgba(139, 92, 246, 0.30)";
const TAKE_GLOW_EDGE = "rgba(139, 92, 246, 0.14)";

/**
 * La lueur, qui descend du haut de la scène.
 *
 * # Pourquoi elle déborde, et pourquoi c'est ce qui la rend belle
 *
 * Elle a d'abord été bornée à la boîte, un rayon de 46 % sur 50 %, centrée. On
 * voyait alors la cloche entière, retombée comprise, et une cloche entière
 * posée au milieu d'un écran est un pâté, pas une lumière : le cœur est vif, le
 * bord est terne, et l'anneau terne se lit comme un contour.
 *
 * L'ambiance de l'exercice d'accords, qui est le modèle, ne borne rien. Son
 * ellipse déborde très au-delà de la zone, si bien qu'on n'en voit **que le
 * haut de la cloche** : plus diffus, sans contour, et plus intense à alpha
 * égal puisque la partie faible tombe hors champ. C'est la même géométrie qui
 * est reprise ici, avec ses deux différences propres à cette scène.
 *
 * Elle est **haute**, centrée à 28 % de la hauteur : la lumière vient d'au-
 * dessus du nom de l'accord et descend sur les notes, au lieu de sourdre du
 * milieu. C'est ce que fait le modèle en se centrant à 40 %, accentué parce que
 * cette scène est plus haute que la sienne.
 *
 * Elle **s'éteint avant le bas**, à 70 % de son rayon, pour ne pas passer
 * derrière la barre du temps ni derrière les doigtés, qui sont la seule chose
 * de cet écran qu'on lit chiffre par chiffre.
 *
 * Une fonction et non trois littéraux : les trois couches doivent partager la
 * même géométrie au pixel près, sans quoi la montée se lirait comme une seconde
 * lumière qui s'allume derrière la première au lieu de la même qui grandit.
 */
function sceneGlow(core: string, edge: string): string {
  return `radial-gradient(ellipse 94% 86% at 50% 28%, ${core} 0%, ${edge} 30%, transparent 70%)`;
}

/**
 * La position des deux accords de l'en-tête, et la place qu'elle demande.
 *
 * `shown` est vrai dès qu'un accord de l'unité est renversé, et il réserve la
 * ligne pour toute l'unité : une hauteur qui n'apparaîtrait que sur les accords
 * renversés ferait sauter les lettres en dessous d'un accord à l'autre. Voir
 * `positionLabel` pour le silence de l'état fondamental.
 */
export function ChordNotes({
  columns,
  chordId,
  name,
  bass,
  inversion,
  numeral,
  holdMs,
  frieze,
  beatTicks,
  struck,
  meter,
  bpm,
  explanation,
}: {
  readonly columns: readonly ChordNoteColumn[];
  /** L'identité de l'accord, qui rejoue la cascade et le compte à rebours. */
  readonly chordId: string;
  /**
   * L'accord en toutes lettres, dans la notation des notes, en deux morceaux :
   * la note et ce qui est empilé dessus. Les deux ne sont pas de la même
   * couleur, d'où la paire plutôt qu'une chaîne.
   */
  readonly name: ChordProse;
  /**
   * La note sur laquelle l'accord se pose, sans conversion de notation, ou
   * `null` quand c'est sa fondamentale.
   */
  readonly bass: string | null;
  /** Le rang de la position de l'accord, dont la pastille tire sa couleur. */
  readonly inversion: number;
  /**
   * Le chiffre romain de l'accord, ou `""` quand l'analyseur n'en répond pas
   * pour ce morceau. Vide, le degré ne se dessine pas du tout : pas de cadre
   * creux, pas de point d'interrogation.
   */
  readonly numeral: string;
  /** Combien de temps le morceau sonne sous cet accord, en millisecondes. */
  readonly holdMs: number;
  /** Tout ce que le niveau donne à jouer, bout à bout : le ruban du rail. */
  readonly frieze: Frieze;
  /** La durée d'un temps, en ticks, dont le rail tire son zoom. */
  readonly beatTicks: number;
  /** Le joueur a commencé la fenêtre : la lueur d'ambiance seule le lit. */
  readonly struck: boolean;
  /** Le chiffrage du morceau, ou `null` quand il n'en publie pas. */
  readonly meter: string | null;
  /** Le tempo **entendu**, ralenti compris, ou `null`. */
  readonly bpm: number | null;
  /** Ce que le niveau ajoute sur cet accord, en une phrase, ou `null`. */
  readonly explanation: string | null;
}) {
  const toNoteLabel = useNoteLabel();
  const pressed = useActivePianoNotes();

  const notes = useMemo<BigChordNote[]>(
    () =>
      columns.map((column) => ({
        id: String(column.pitchClass),
        name: column.name,
        root: column.root,
        /*
          La touche de la **droite**, et elle seule.

          `midis` réunit les deux mains, et l'exiger en entier était juste tant
          que la gauche doublait la fondamentale de l'accord au même instant.
          Depuis que la basse écrite est là, la gauche prend cette classe à son
          propre moment : la colonne réclamait alors deux touches qui ne sonnent
          jamais ensemble, et la lettre ne s'allumait plus de la fenêtre.

          Chaque main s'allume désormais là où elle est décrite. La ligne
          d'accord est la pile de la droite, donc elle répond de la droite ; ce
          que fait la gauche se lit sur la basse, à côté.
        */
        held: column.right !== null && pressed.has(column.right.midi),
        right: column.right === null ? null : { finger: column.right.finger, down: pressed.has(column.right.midi) },
        left: column.left === null ? null : { finger: column.left.finger, down: pressed.has(column.left.midi) },
      })),
    [columns, pressed],
  );

  // Rien ne s'allume pendant le décompte : une frappe avant le premier temps
  // lancerait le passage par-dessus le compte.
  const glow = takeGlow({ armed: true, struck, holdMs });

  return (
    <div
      className="flex items-center justify-center"
      style={{
        // `container-type: size` retire le contenu du calcul de taille : sans
        // hauteur définie venant du parent, la boîte tombe à zéro et les `cqh`
        // avec elle. D'où `alignSelf: stretch`, qui va chercher la hauteur que
        // la scène accorde à cette zone.
        position: "relative",
        containerType: "size",
        flex: "1 1 auto",
        alignSelf: "stretch",
        minHeight: 0,
        overflow: "hidden",
        width: "100%",
      }}
    >
      {/* Le repos : ce que la scène montre quand rien n'est sous les mains. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: sceneGlow(SCENE_GLOW_CORE, SCENE_GLOW_EDGE) }}
      />

      {/*
        Le passage qui monte.

        Toujours montée, jamais démontée : c'est la transition d'une couche qui
        reste en place qui porte l'information, et un montage la ferait
        apparaître à sa valeur d'arrivée sans jamais la parcourir. Elle retombe
        au changement d'accord, où l'écran se remet à attendre.
      */}
      <div
        aria-hidden
        className="chord-take-glow pointer-events-none absolute inset-0"
        style={{
          background: sceneGlow(TAKE_GLOW_CORE, TAKE_GLOW_EDGE),
          opacity: glow.rise,
          transitionDuration: `${Math.round(glow.riseMs)}ms`,
        }}
      />

      {/*
        La marge du contenu, portée ici et non par la scène : les couches de
        lueur sont posées sur toute la boîte, donc une marge au-dessus d'elles
        les tiendrait à distance des bords et la lumière s'arrêterait net sur
        un cadre noir.
      */}
      <div
        className="relative flex w-full flex-col items-center"
        style={{
          // Toute la hauteur, et c'est ce qui ancre le rail : la boîte se
          // remplit, le rail se pose au fond, et le reste se centre dans ce
          // qui demeure.
          alignSelf: "stretch",
          maxWidth: "100%",
          /*
            Aucun dégagement sous la partition, et c'est la tête de lecture qui
            l'exige. Sa pointe du bas est le repère qu'on retrouve d'un coup
            d'œil ; posée à dix ou vingt pixels du bord, elle flotte, et l'œil
            doit la chercher au lieu de la trouver là où l'écran s'arrête. Le
            dégagement existait pour qu'un rail collé au clavier ne se lise pas
            comme une rangée de touches de plus : une portée à cinq lignes ne
            court pas ce risque.
          */
          padding: `4px ${CONTENT_PAD}px 0`,
        }}
      >
        {/*
          Le nom et les lettres, centrés dans la place que le rail laisse.

          `margin: auto 0` sur le groupe : en colonne flex, une marge
          automatique absorbe l'espace libre des deux côtés, donc le groupe se
          centre entre le haut de la boîte et le rail sans qu'on ait à répartir
          quoi que ce soit à la main.
        */}
        <div
          className="flex w-full flex-col items-center"
          style={{
            margin: "auto 0",
            // Le plancher est ce qui compte ici, pas la part : cette zone fait
            // quatre cents pixels de haut, donc `1.6cqh` rendait six pixels et
            // les lettres venaient se coller au nom. La part est relevée pour
            // que les grands écrans respirent, et le plancher pour que les
            // petits ne collent pas.
            gap: "clamp(10px, 3cqh, 28px)",
          }}
        >
          {/*
            Le nom et les lettres se taisent quand le niveau n'a pas d'accord
            sous les mains, ce qui n'arrive qu'en lecture : le transport passe
            avant la première fenêtre de l'unité, ou après la dernière.

            Le groupe, lui, reste monté et garde ses marges automatiques. Sans
            lui la partition remonterait au haut de la boîte, et l'écran ferait
            ce saut à chaque tour de boucle. C'est la même règle que pour le
            rail : rien de cet écran n'apparaît ni ne disparaît sous les doigts.
          */}
          {columns.length > 0 && (
            <>
              {/* Le nom respire au-dessus des lettres : collés, on lit une seule
                masse de texte au lieu d'un titre et de ce qu'il désigne. */}
              <div className="w-full" style={{ marginBottom: "clamp(5px, 2cqh, 18px)" }}>
                <Heading
                  key={`name:${chordId}`}
                  name={name}
                  bass={bass === null ? null : toNoteLabel(bass)}
                  inversion={inversion}
                  numeral={numeral}
                />
              </div>

              {/*
                Les seules notes de l'accord, centrées sous leur nom.

                La rangée a porté la basse à sa gauche, derrière un séparateur,
                ce qui lui demandait trois pistes : le dessin au milieu pour
                qu'il reste sur l'axe du nom, et les deux extrêmes à parts
                égales pour le centrer quoi que porte la basse. La basse est
                passée sur le rail, où l'on peut la lire en jouant, et la rangée
                redevient ce qu'elle dit être.
              */}
              <BigChordNotes
                notes={notes}
                resetKey={chordId}
                // Le nom et le compte à rebours prennent leur part de la
                // hauteur : les notes reviennent au plafond de l'écran
                // d'exercice d'accords, dont le panneau porte lui aussi un
                // en-tête au-dessus d'elles.
                heightCap={40}
                // La mesure des lettres, que la boîte ne porte pas.
                measure={NOTES_MEASURE}
                // Sans les deux rangées de doigtés : elles coûtaient deux
                // lignes sur un écran qui en manque, et le vrai clavier, juste
                // dessous, montre déjà les touches allumées main par main. Le
                // point sous chaque colonne reste, lui : il dit que la note est
                // prise.
                fingers={false}
                ariaLabel={`Accord à prendre : ${columns.map((column) => toNoteLabel(column.name)).join(", ")}`}
              />
            </>
          )}

          {/*
            Ce que le niveau ajoute ici, entre l'accord et la partition.

            Toujours monté, avec deux lignes réservées : la phrase se tait sur
            la plupart des accords, et sans sa hauteur l'accord sauterait
            au-dessus d'elle à chaque changement, sous les yeux de quelqu'un
            qui joue.
          */}
          <div
            className="flex flex-col items-center"
            style={{
              gap: "clamp(5px, 0.9vh, 10px)",
              maxWidth: "100%",
              margin: "clamp(20px, 4.4vh, 46px) 0 clamp(10px, 2.4vh, 26px)",
              textAlign: "center",
            }}
          >
            <span
              aria-hidden={explanation === null}
              style={{
                fontFamily: FONT_MONO,
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: "0.26em",
                textTransform: "uppercase",
                color: EXPLANATION_LABEL_INK,
                visibility: explanation === null ? "hidden" : "visible",
              }}
            >
              Concept harmonique
            </span>
            <p
              style={{
                margin: 0,
                minHeight: EXPLANATION_HEIGHT,
                maxWidth: EXPLANATION_MEASURE,
                fontSize: EXPLANATION_SIZE,
                fontWeight: 200,
                lineHeight: EXPLANATION_LINE_HEIGHT,
                letterSpacing: "0.012em",
                color: EXPLANATION_INK,
                textWrap: "balance",
              }}
            >
              {explanation}
            </p>
          </div>

          {/*
          Le rail est là avant la première frappe, éteint : il dit ce qu'il y
          aura à jouer, ce qui est la seule chose qu'on veuille savoir à ce
          moment-là. Un objet qui apparaîtrait à la frappe ferait sauter le
          bloc entier sous les doigts, au pire moment.

          Ce qui vient est écrit dessous, et non plus à côté du nom : c'est là
          que le regard est pendant qu'on joue, donc c'est là qu'il faut le
          poser pour qu'il serve à préparer la main.
        */}
          {/*
          Toute la largeur, et pas seulement celle du contenu : le rail se
          dimensionne en `min(100%, …)`, et dans une colonne centrée qui se
          règle sur son contenu ce pourcentage n'a plus de référence. Le rail
          tombait alors à la largeur de sa propre ligne d'en-tête.
        */}
        </div>

        {/*
          Le rail au fond de la boîte, et il n'en bouge plus.

          C'est ce qui le rend lisible du coin de l'oeil : on ne cherche pas un
          objet qui se déplace d'un accord à l'autre selon la longueur du nom
          au-dessus. Il est toujours là, juste au-dessus du clavier, et la
          vision périphérique n'a qu'un endroit à surveiller.
        */}
        <div className="flex w-full flex-col items-center">
          {/*
            Sans `key` sur l'accord, et c'est le changement : le rail n'est plus
            une fenêtre qu'on remplace mais un ruban qui se déroule. Le
            remonter à chaque accord lui ferait perdre la position que la
            boucle de rendu vient d'y écrire, une fois par accord.
          */}
          <ScoreRail frieze={frieze} beatTicks={beatTicks} meter={meter} bpm={bpm} />
        </div>
      </div>
    </div>
  );
}

/**
 * L'accord, son degré, et ce qui vient.
 *
 * Remonté à chaque accord, comme les notes : la cascade et l'en-tête arrivent
 * ensemble, sinon le nom changerait avant les lettres qu'il désigne.
 */
function Heading({
  name,
  bass,
  inversion,
  numeral,
}: {
  readonly name: ChordProse;
  /** La barre du symbole, déjà dans la notation des notes, ou `null`. */
  readonly bass: string | null;
  readonly inversion: number;
  readonly numeral: string;
}) {
  /*
    Trois colonnes dont les deux extrêmes sont égales, et c'est ce qui centre.

    Le nom courant est ce que l'écran affirme : il doit tomber sur l'axe des
    notes qu'il désigne. Une rangée en `justify-center` centrerait la ligne
    entière, donc le nom se décalerait dès qu'un badge apparaît d'un côté.

    `minmax(0, 1fr)` de chaque côté : les deux pistes se partagent le reste à
    parts égales quoi qu'elles contiennent, donc la colonne `auto` du milieu est
    exactement centrée. Le degré prend la piste de gauche calé à droite, la
    position la piste de droite calée à gauche : les deux se posent contre le
    nom sans jamais entrer dans sa piste.
  */
  return (
    <div
      className="chord-note-drop"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
        alignItems: "center",
        width: "100%",
        columnGap: 22,
      }}
    >
      {numeral === "" ? <span /> : <DegreeBadge numeral={numeral} />}

      <span
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: NAME_SIZE,
          fontWeight: 300,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          color: "var(--color-text-primary)",
          whiteSpace: "nowrap",
        }}
      >
        {/* Le violet du degré juste à gauche, et celui de la lueur : la note
            de l'accord, son chiffre romain et la lumière de la scène sont une
            seule teinte, donc une seule chose à retenir. */}
        <span style={{ color: VIOLET_BRIGHT }}>{name.root}</span>
        {name.quality !== null && ` ${name.quality}`}
        {/* La basse que la main gauche joue, dans sa couleur : c'est la barre
            du symbole, et elle appartient à l'autre main. Plus petite, pour
            que le nom reste ce qu'on lit d'abord. */}
        {bass !== null && (
          <span style={{ fontSize: "0.55em", color: "var(--color-hand-left)" }}>
            {" / "}
            {bass}
          </span>
        )}
      </span>

      <InversionPill inversion={inversion} />
    </div>
  );
}

/**
 * La position de l'accord, en pastille contre son nom.
 *
 * ## Pourquoi elle a quitté la ligne du dessous
 *
 * « Deuxième renversement » y était écrit dans la petite capitale grise des
 * légendes, à trente pixels sous le nom. C'était lisible à l'arrêt et invisible
 * en jouant : l'œil est sur le rail de rythme, et il ne remonte au nom que le
 * temps d'un coup d'œil. Une pastille assez grande, contre le nom, se prend
 * dans ce coup d'œil-là ; une ligne de légende non.
 *
 * ## La couleur avant les mots
 *
 * Les trois teintes sont celles que le produit garde pour une échelle à trois
 * degrés : vert, or, rouge, du plus proche du repos au plus loin. Elles ne
 * jugent rien — un deuxième renversement n'est pas une faute — elles ordonnent,
 * et c'est précisément ce qu'une position est : la fondamentale s'éloigne de la
 * basse d'un cran à chaque fois.
 *
 * Elles ne peuvent pas se confondre avec le code de fonction du badge de degré,
 * qui vit de l'autre côté du nom, dans un carré, avec un chiffre romain.
 */
function InversionPill({
  inversion,
  small = false,
}: {
  readonly inversion: number;
  /** Celle de l'accord qui vient, à l'échelle de l'annonce et non du nom. */
  readonly small?: boolean;
}) {
  const color = INVERSION_COLOR[inversion] ?? INVERSION_COLOR[0]!;
  const full = positionLabel(inversion);

  return (
    <span
      className="font-mono uppercase"
      style={{
        justifySelf: "start",
        alignSelf: "center",
        whiteSpace: "nowrap",
        fontSize: small ? "0.72em" : "min(clamp(12px, 1.7cqw, 20px), 6.5cqh)",
        letterSpacing: "0.14em",
        lineHeight: 1,
        padding: small ? "0.5em 0.7em 0.44em" : "0.62em 0.85em 0.55em",
        borderRadius: 999,
        color,
        background: fade(color, 0.14),
        border: `1px solid ${fade(color, 0.4)}`,
      }}
      aria-label={full === null ? "Position fondamentale" : full}
    >
      {positionShort(inversion)}
    </span>
  );
}

/**
 * Les trois crans de l'échelle des positions, par rang de la fondamentale.
 *
 * Repris de la palette du produit plutôt qu'inventés ici : ce sont déjà les
 * trois couleurs qu'il emploie quand il ordonne quelque chose.
 */
const INVERSION_COLOR: Record<number, string> = {
  0: "rgba(255, 255, 255, 0.72)",
  1: GREEN,
  2: GOLD,
  3: RED,
};

/** La même couleur, en fond ou en trait : une teinte, deux opacités. */
function fade(color: string, alpha: number): string {
  if (color.startsWith("rgba")) {
    return color.replace(/[\d.]+\)$/, `${alpha})`);
  }

  const hex = color.slice(1);
  const to = (at: number) => parseInt(hex.slice(at, at + 2), 16);

  return `rgba(${to(0)}, ${to(2)}, ${to(4)}, ${alpha})`;
}

/**
 * Le degré, dans son cadre, à gauche du nom.
 *
 * ## Sans la fonction
 *
 * Le carré a porté le sigle de la fonction sous le chiffre — `T`, `SD`, `D`.
 * Il le disait une seconde fois : le bandeau, deux lignes plus haut, écrit
 * « T · TONIQUE » en toutes lettres et explique ce que ça veut dire. Deux
 * emplacements pour une seule information, dont l'un tient en une lettre, et
 * c'est celui-là qui coûtait de la hauteur au cadre.
 */
function DegreeBadge({ numeral }: { readonly numeral: string }) {
  return (
    <span
      aria-hidden
      className="flex items-center justify-center"
      style={{
        justifySelf: "end",
        alignSelf: "center",
        flexShrink: 0,
        fontFamily: FONT_MONO,
        // Réduit d'un cinquième depuis qu'il n'a plus qu'une ligne à tenir : à
        // l'ancien corps, un carré qui ne porte qu'un chiffre pèse plus que le
        // nom qu'il annote.
        fontSize: "min(clamp(17px, 2.7cqw, 34px), 8cqh)",
        width: "1.9em",
        height: "1.9em",
        borderRadius: "0.34em",
        background: DEGREE_BADGE_FILL,
        border: `1px solid ${DEGREE_BADGE_EDGE}`,
      }}
    >
      <span style={{ fontSize: "1em", lineHeight: 1, color: VIOLET_BRIGHT }}>{numeral}</span>
    </span>
  );
}

/** Le cadre du degré : le violet de la scène, au fond et au trait. */
const DEGREE_BADGE_FILL = "rgba(139, 92, 246, 0.13)";
const DEGREE_BADGE_EDGE = "rgba(139, 92, 246, 0.35)";
